/**
 * Campos rellenados por el navegador o por un gestor de contraseñas
 * -----------------------------------------------------------------------------
 * El label flotante de MDB depende de la clase `active` en el `<input>`, y esa
 * clase se decide con UNA comprobación del valor en `DOMContentLoaded`. El
 * autorrelleno llega cuando llega: a veces antes y a veces después. De ahí que
 * el label aparezca encima del texto solo en algunas recargas.
 *
 * El aspecto ya lo arregla `_autofill.scss` sin JavaScript. Esto se encarga de
 * que el ESTADO del componente coincida con lo que se ve, que es lo que evita el
 * salto al primer clic y que un `blur` posterior deje el label a medio camino.
 *
 * Dos vías, porque hay dos formas de que un campo se llene solo:
 *
 *   1. El navegador. Marca el campo con `:-webkit-autofill` / `:autofill`, y
 *      gracias a la animación declarada en el SCSS dispara `animationstart` en
 *      ese preciso momento. No hay que sondear nada ni esperar a que el usuario
 *      haga algo.
 *
 *   2. Un gestor externo (1Password, Bitwarden…). Algunos escriben el valor
 *      directamente y no disparan `input` ni activan la pseudoclase. Para esos
 *      se revisa una vez en cuanto el usuario toca la página: ese gesto es
 *      además el que hace que Chrome deje de ocultar el valor a los scripts.
 */

const ANIMATION = 'company-autofill';

let mdbComponents = null;
let installed = false;

/** Pone `active` si el campo tiene contenido, sin depender de eventos previos. */
function sync(input) {
  if (!input || input.classList.contains('active')) return;

  const wrapper = input.closest('.form-outline');
  if (!wrapper) return;

  const Input = mdbComponents && mdbComponents.Input;
  const instance = Input && Input.getInstance(wrapper);

  if (instance) {
    instance.forceActive();
    return;
  }

  // Sin instancia de MDB —markup pintado por la app sin inicializar— al menos
  // el label queda bien.
  input.classList.add('active');
}

/** Todos los campos con contenido que aún no estén marcados. */
function syncAll(root) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;

  scope.querySelectorAll('.form-outline input, .form-outline textarea').forEach((input) => {
    if (input.value !== '') sync(input);
  });
}

/**
 * @param {ParentNode} [root=document]
 * @param {Object} [components] Componentes de MDB.
 */
export function setupAutofill(root = document, components = null) {
  if (components) mdbComponents = components;

  // Lo ya autorrellenado antes de llegar aquí. `animationstart` solo avisa de lo
  // que ocurre a partir de ahora.
  syncAll(root);

  if (installed || typeof document === 'undefined') return;
  installed = true;

  // Vía 1: el navegador acaba de autorrellenar y lo marca con la pseudoclase,
  // que dispara la animación declarada en el SCSS.
  document.addEventListener(
    'animationstart',
    (event) => {
      if (event.animationName !== ANIMATION) return;
      sync(event.target);
    },
    true
  );

  // Vía 2: valores que aparecen sin avisar de ninguna forma.
  //
  // No todo lo que rellena un campo activa la pseudoclase ni dispara `input`:
  // la restauración de formularios al recargar no lo hace, y algunos gestores
  // de contraseñas tampoco. Ahí no hay ningún evento al que engancharse, así
  // que se repasa unas cuantas veces durante el primer segundo.
  //
  // Es una ventana corta y acotada, no un sondeo permanente: pasado ese rato
  // manda el repaso al primer gesto del usuario, y a partir de ahí los eventos
  // normales de MDB ya se ocupan. Cada pasada solo mira el `value` de los
  // campos que aún no están marcados.
  [0, 120, 350, 700, 1200].forEach((delay) => {
    window.setTimeout(() => syncAll(document), delay);
  });

  // `load` llega después de `DOMContentLoaded`, que es donde MDB hace su única
  // comprobación: es justo la franja en la que se cuela el valor tardío.
  window.addEventListener('load', () => syncAll(document));

  // Volver con el botón atrás restaura la página entera desde la caché, con los
  // campos ya rellenos y sin repetir `DOMContentLoaded`.
  window.addEventListener('pageshow', () => syncAll(document));

  // Y el último recurso: el primer gesto del usuario. Es además el momento en
  // que Chrome deja de ocultar a los scripts el valor autorrellenado.
  const onFirstGesture = () => syncAll(document);

  ['pointerdown', 'keydown', 'touchstart'].forEach((type) => {
    document.addEventListener(type, onFirstGesture, { once: true, capture: true });
  });
}

export default setupAutofill;
